// 2026-06-26: Phase 14 — Calibration Register (NADCAP Common Core item 5)
// Tracks measurement instruments, calibration dates, and due-state across the floor.
// Data lives in customer SERVER_SCOPE attribute `calibration_register` (array).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

const TYPES = [
  'Gauge — Plug', 'Gauge — Ring', 'Gauge — Snap', 'Gauge — Vernier',
  'Micrometer', 'Caliper', 'Dial Indicator', 'Height Gauge',
  'CMM Probe', 'Thermocouple', 'Pressure Gauge', 'Torque Wrench',
  'Pyrometer', 'Hardness Tester', 'Surface Roughness', 'Other',
];

const STATUS_INFO = {
  VALID:   { color: BR.green, bg: '#dcfce7', label: 'VALID',   icon: <VerifiedIcon fontSize="small" /> },
  WARNING: { color: BR.amber, bg: '#fef3c7', label: 'DUE SOON', icon: <WarningAmberIcon fontSize="small" /> },
  OVERDUE: { color: BR.red,   bg: '#fee2e2', label: 'OVERDUE', icon: <ErrorOutlineIcon fontSize="small" /> },
};

function computeStatus(nextCalDue) {
  if (!nextCalDue) return 'OVERDUE';
  const due = new Date(nextCalDue);
  if (isNaN(due.getTime())) return 'OVERDUE';
  const now = new Date();
  const daysLeft = (due - now) / (24 * 3600 * 1000);
  if (daysLeft < 0) return 'OVERDUE';
  if (daysLeft <= 30) return 'WARNING';
  return 'VALID';
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysFromNow(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  const days = Math.round((dt - new Date()) / (24 * 3600 * 1000));
  return days;
}

const emptyInstrument = {
  id: '',
  instrument_id: '',
  instrument_type: '',
  serial_no: '',
  description: '',
  location: '',
  last_cal_date: '',
  next_cal_due_date: '',
  cal_interval_months: 12,
  calibration_lab: '',
  certificate_no: '',
  notes: '',
};

export default function CalibrationRegister() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyInstrument);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await customerbasedshift(customerId, 'calibration_register');
      const v = Array.isArray(r?.[0]?.value) ? r[0].value : [];
      setItems(v);
    } catch (e) {
      console.warn('calibration register fetch failed:', e?.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const enriched = useMemo(() => items.map(i => ({
    ...i,
    status: computeStatus(i.next_cal_due_date),
    days_until_due: daysFromNow(i.next_cal_due_date),
  })), [items]);

  const totals = useMemo(() => {
    const out = { ALL: enriched.length, VALID: 0, WARNING: 0, OVERDUE: 0 };
    enriched.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return (i.instrument_id || '').toLowerCase().includes(q)
          || (i.serial_no || '').toLowerCase().includes(q)
          || (i.instrument_type || '').toLowerCase().includes(q)
          || (i.location || '').toLowerCase().includes(q);
    }).sort((a, b) => {
      const order = { OVERDUE: 0, WARNING: 1, VALID: 2 };
      const so = (order[a.status] || 9) - (order[b.status] || 9);
      if (so) return so;
      return (a.days_until_due ?? 9999) - (b.days_until_due ?? 9999);
    });
  }, [enriched, filter, search]);

  function openAdd() {
    setDraft({ ...emptyInstrument, id: `cal-${Date.now()}` });
    setEditOpen(true);
  }

  function openEdit(item) {
    setDraft({ ...emptyInstrument, ...item });
    setEditOpen(true);
  }

  async function save() {
    if (!draft.instrument_id) {
      Swal.fire({ icon: 'warning', title: 'Instrument ID required', confirmButtonColor: BR.orange });
      return;
    }
    if (!draft.last_cal_date || !draft.next_cal_due_date) {
      Swal.fire({ icon: 'warning', title: 'Calibration dates required',
                  text: 'Please set both last cal date and next cal due date',
                  confirmButtonColor: BR.orange });
      return;
    }
    setSaving(true);
    try {
      const exists = items.findIndex(i => i.id === draft.id);
      const next = exists === -1
        ? [...items, draft]
        : items.map((it, idx) => idx === exists ? draft : it);
      await shiftadd({ calibration_register: next }, customerId, 'SERVER_SCOPE');
      setItems(next);
      setEditOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                   icon: 'success', title: `${draft.instrument_id} saved` });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed',
                   text: e?.response?.data?.message || e.message,
                   confirmButtonColor: BR.orange });
    } finally {
      setSaving(false);
    }
  }

  async function deleteItem(item) {
    const res = await Swal.fire({
      title: 'Delete instrument?',
      html: `<strong>${item.instrument_id}</strong> · ${item.instrument_type || 'instrument'}<br/>This removes the calibration record from the register.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Delete', confirmButtonColor: BR.red,
      cancelButtonText: 'Cancel', reverseButtons: true, focusCancel: true,
    });
    if (!res.isConfirmed) return;
    try {
      const next = items.filter(i => i.id !== item.id);
      await shiftadd({ calibration_register: next }, customerId, 'SERVER_SCOPE');
      setItems(next);
      Swal.fire({ toast: true, position: 'top-end', timer: 1500, showConfirmButton: false,
                   icon: 'success', title: 'Removed' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: e.message, confirmButtonColor: BR.orange });
    }
  }

  function recalcDueFromInterval(lastDate, months) {
    if (!lastDate || !months) return '';
    const d = new Date(lastDate);
    if (isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() + Number(months));
    return d.toISOString().slice(0, 10);
  }

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>
            🛠 Calibration Register
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Track gauges, sensors and test equipment · NADCAP Common Core capability #5
          </div>
        </div>
        <button onClick={openAdd}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '9px 16px', background: BR.orange, color: '#fff',
                          border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13,
                          cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Add Instrument
        </button>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL',     label: 'All', icon: '📋', color: BR.dark },
          { key: 'VALID',   label: 'Valid', icon: '✓', color: BR.green },
          { key: 'WARNING', label: 'Due Soon', icon: '⚠', color: BR.amber },
          { key: 'OVERDUE', label: 'Overdue', icon: '✗', color: BR.red },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
               style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`,
                        borderRadius: 8, padding: '12px 14px', cursor: 'pointer',
                        boxShadow: filter === t.key ? `0 2px 8px ${t.color}33` : '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t.icon} {t.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {totals[t.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search ID, serial, type, location…"
               style={{ flex: 1, padding: '8px 12px', border: `1px solid ${BR.border}`,
                         borderRadius: 6, fontSize: 13 }} />
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No instruments {filter !== 'ALL' ? `in ${filter} state` : 'yet'}. Click "Add Instrument" to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status', 'Instrument ID', 'Type', 'Serial', 'Location', 'Last Cal', 'Next Due', 'Days Left', 'Cert #', 'Lab', ''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.VALID;
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.icon} {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{i.instrument_id}</td>
                    <td style={{ padding: '9px 10px' }}>{i.instrument_type || '—'}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: BR.muted }}>{i.serial_no || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>{i.location || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>{fmtDate(i.last_cal_date)}</td>
                    <td style={{ padding: '9px 10px', color: info.color, fontWeight: 700 }}>{fmtDate(i.next_cal_due_date)}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 800, color: info.color }}>
                      {i.days_until_due == null ? '—' : `${i.days_until_due}d`}
                    </td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{i.certificate_no || '—'}</td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{i.calibration_lab || '—'}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(i)}>
                        <EditIcon fontSize="small" sx={{ color: '#111' }} />
                      </IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteItem(i)}>
                        <DeleteIcon fontSize="small" sx={{ color: BR.red }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#fff7ed', border: `1px solid #fed7aa`, borderRadius: 6 }}>
        <strong style={{ color: '#9a3412' }}>NADCAP note:</strong> Instruments marked <strong>OVERDUE</strong> must not be used for accepting product. Future gating: operation start will be blocked when an instrument tagged in a process recipe is overdue (Common Core #5, audit-grade).
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {items.some(i => i.id === draft.id) ? `Edit ${draft.instrument_id}` : 'Add Instrument'}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Instrument ID *" size="small" fullWidth
                       value={draft.instrument_id}
                       onChange={e => setDraft({ ...draft, instrument_id: e.target.value })}
                       placeholder="e.g. GA-MIC-014" />
            <TextField label="Type" size="small" fullWidth select
                       value={draft.instrument_type}
                       onChange={e => setDraft({ ...draft, instrument_type: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Serial No" size="small" fullWidth
                       value={draft.serial_no}
                       onChange={e => setDraft({ ...draft, serial_no: e.target.value })} />
            <TextField label="Location" size="small" fullWidth
                       value={draft.location}
                       onChange={e => setDraft({ ...draft, location: e.target.value })}
                       placeholder="e.g. CMM Room, Cell-8" />
            <TextField label="Last Calibration Date *" size="small" fullWidth type="date"
                       value={draft.last_cal_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => {
                         const lcd = e.target.value;
                         const nxt = recalcDueFromInterval(lcd, draft.cal_interval_months);
                         setDraft({ ...draft, last_cal_date: lcd, next_cal_due_date: nxt || draft.next_cal_due_date });
                       }} />
            <TextField label="Interval (months)" size="small" fullWidth type="number"
                       value={draft.cal_interval_months}
                       onChange={e => {
                         const m = e.target.value;
                         const nxt = recalcDueFromInterval(draft.last_cal_date, m);
                         setDraft({ ...draft, cal_interval_months: m, next_cal_due_date: nxt || draft.next_cal_due_date });
                       }} />
            <TextField label="Next Due Date *" size="small" fullWidth type="date"
                       value={draft.next_cal_due_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, next_cal_due_date: e.target.value })} />
            <TextField label="Certificate No" size="small" fullWidth
                       value={draft.certificate_no}
                       onChange={e => setDraft({ ...draft, certificate_no: e.target.value })} />
            <TextField label="Calibration Lab" size="small" fullWidth
                       value={draft.calibration_lab}
                       onChange={e => setDraft({ ...draft, calibration_lab: e.target.value })}
                       placeholder="e.g. NABL-accredited lab" />
            <TextField label="Description" size="small" fullWidth
                       value={draft.description}
                       onChange={e => setDraft({ ...draft, description: e.target.value })} />
            <TextField label="Notes" size="small" fullWidth multiline minRows={2}
                       value={draft.notes}
                       onChange={e => setDraft({ ...draft, notes: e.target.value })}
                       sx={{ gridColumn: '1 / span 2' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
                  sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
